import type { FC } from 'react';
import { useState } from 'react';
import {
	Modal,
	Platform,
	StyleSheet,
	Text,
	TouchableOpacity,
	View,
	type StyleProp,
	type ViewStyle,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Constants from 'src/core/utils/Constants';
import Translations from 'src/core/utils/Translations';
import SvgIcon from 'src/presentation/components/atoms/SvgIcon';
import { SvgIconType } from '@thermenresorts/icons';

export type PickerItem = {
	label: string;
	value: string | null;
};

export type Props = {
	style?: StyleProp<ViewStyle>;
	items: PickerItem[];
	value: string | undefined;
	placeholder?: PickerItem | null;
	onValueChange: (value: string, index: number) => void;
};

if (
	Platform.OS === Constants.platformWeb &&
	typeof document !== 'undefined' &&
	!document.getElementById(Constants.selectFieldStyleElementId)
) {
	const style = document.createElement('style');
	style.id = Constants.selectFieldStyleElementId;
	style.textContent = Constants.selectFieldWebResetCss;
	document.head.appendChild(style);
}

const SelectField: FC<Props> = ({ style, items, value, placeholder, onValueChange }) => {
	const [iosModalVisible, setIosModalVisible] = useState(false);
	const [iosPendingValue, setIosPendingValue] = useState<string | undefined>(value);

	const allItems: PickerItem[] = placeholder ? [placeholder, ...items] : items;
	const selectedLabel =
		allItems.find((item) => item.value === value)?.label ?? placeholder?.label ?? allItems[0]?.label ?? '';

	if (Platform.OS === Constants.platformIos) {
		const handleDone = () => {
			setIosModalVisible(false);

			const picked = iosPendingValue ?? allItems[0]?.value;

			if (picked === undefined || picked === null) {
				return;
			}

			const pendingIndex = allItems.findIndex((item) => item.value === picked);
			onValueChange(picked, pendingIndex >= 0 ? pendingIndex : 0);
		};

		const handleOpen = () => {
			setIosPendingValue(value);
			setIosModalVisible(true);
		};

		return (
			<View style={style}>
				<TouchableOpacity style={styles.inputContainer} onPress={handleOpen}>
					<Text style={[styles.inputText, !value && styles.placeholderText]}>{selectedLabel}</Text>
					<View style={styles.iconContainer}>
						<SvgIcon name={SvgIconType.chevronDown} size={Dimens.iconSize12} tintColor={Colors.gray600} />
					</View>
				</TouchableOpacity>

				<Modal
					animationType="slide"
					transparent={true}
					visible={iosModalVisible}
					onRequestClose={() => setIosModalVisible(false)}
				>
					<TouchableOpacity
						activeOpacity={Dimens.opacity100}
						style={styles.iosBackdrop}
						onPress={() => setIosModalVisible(false)}
					/>

					<View style={styles.iosPickerPanel}>
						<View style={styles.iosToolbar}>
							<TouchableOpacity style={styles.iosDoneButton} onPress={handleDone}>
								<Text style={styles.iosDoneText}>{Translations.done}</Text>
							</TouchableOpacity>
						</View>
						<Picker
							itemStyle={styles.iosPickerItem}
							selectedValue={iosPendingValue ?? ''}
							onValueChange={(itemValue) => setIosPendingValue(itemValue)}
						>
							{allItems.map((item, index) => (
								<Picker.Item key={item.value ?? `placeholder-${index}`} label={item.label} value={item.value ?? ''} />
							))}
						</Picker>
					</View>
				</Modal>
			</View>
		);
	}

	if (Platform.OS === Constants.platformWeb) {
		return (
			<View style={[styles.inputContainer, style, styles.webInputContainer]}>
				<Picker
					selectedValue={value ?? ''}
					style={styles.webPicker}
					onValueChange={(itemValue, itemIndex) => {
						onValueChange(itemValue, itemIndex);
					}}
				>
					{allItems.map((item, index) => (
						<Picker.Item key={item.value ?? `placeholder-${index}`} label={item.label} value={item.value ?? ''} />
					))}
				</Picker>
				<View pointerEvents="none" style={styles.webIconContainer}>
					<SvgIcon name={SvgIconType.chevronDown} size={Dimens.iconSize12} tintColor={Colors.gray600} />
				</View>
			</View>
		);
	}

	return (
		<View style={[styles.inputContainer, styles.androidInputContainer, style]}>
			<Picker
				dropdownIconColor={Colors.gray600}
				selectedValue={value ?? ''}
				style={styles.pickerStyle}
				onValueChange={(itemValue, itemIndex) => {
					onValueChange(itemValue, itemIndex);
				}}
			>
				{allItems.map((item, index) => (
					<Picker.Item key={item.value ?? `placeholder-${index}`} label={item.label} value={item.value ?? ''} />
				))}
			</Picker>
		</View>
	);
};

const styles = StyleSheet.create({
	inputContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		borderBottomWidth: Dimens.borderWidth1,
		borderBottomColor: Colors.gray400,
		borderTopLeftRadius: Dimens.borderRadius4,
		borderTopRightRadius: Dimens.borderRadius4,
		overflow: 'hidden',
		backgroundColor: Colors.gray150,
		marginTop: Dimens.padding8,
		paddingVertical: Dimens.padding17,
		paddingHorizontal: Dimens.padding16,
	},
	inputText: {
		flex: Dimens.flex1,
		fontSize: Dimens.fontSize16,
		color: Colors.gray700,
		paddingRight: Dimens.padding8,
	},
	placeholderText: {
		color: Colors.gray800,
	},
	iconContainer: {
		backgroundColor: Colors.gray150,
	},
	iosBackdrop: {
		flex: Dimens.flex1,
		backgroundColor: Colors.black66,
	},
	iosPickerPanel: {
		backgroundColor: Colors.white,
	},
	iosPickerItem: {
		color: Colors.gray700,
	},
	iosToolbar: {
		flexDirection: 'row',
		justifyContent: 'flex-end',
		borderBottomWidth: Dimens.borderWidth1,
		borderBottomColor: Colors.gray200,
		paddingHorizontal: Dimens.padding16,
		paddingVertical: Dimens.padding12,
	},
	iosDoneButton: {
		paddingHorizontal: Dimens.padding8,
	},
	iosDoneText: {
		fontSize: Dimens.fontSize16,
		color: Colors.blue,
		fontWeight: Dimens.fontWeight600,
	},
	androidInputContainer: {
		height: Dimens.size56,
		paddingVertical: Dimens.padding0,
		paddingLeft: Dimens.padding8,
		paddingRight: Dimens.padding0,
	},
	pickerStyle: {
		flex: Dimens.flex1,
		color: Colors.gray700,
	},
	webInputContainer: {
		paddingHorizontal: Dimens.padding0,
	},
	webPicker: {
		flex: Dimens.flex1,
		color: Colors.gray700,
		borderWidth: Dimens.borderWidth0,
		backgroundColor: Colors.transparent,
		fontSize: Dimens.fontSize16,
		paddingLeft: Dimens.padding16,
		paddingRight: Dimens.padding16 + Dimens.iconSize12 + Dimens.padding8,
	},
	webIconContainer: {
		position: 'absolute',
		right: Dimens.padding16,
		alignSelf: 'center',
	},
});

export default SelectField;
